import React, { useCallback, useMemo } from 'react'

import { twMerge } from 'tailwind-merge'

import { useNotification } from 'src/components/shared/Notification/NotificationContext'
import {
  BackgroundJobStatusValue,
  isActiveBackgroundJobStatus,
} from 'src/fetch/backgroundJobs/backgroundJobStatus'
import { Icon, T, VStack } from 'src/govwell-ui'
import type { IconKey } from 'src/govwell-ui/components/Icon/util'
import { useIsGov } from 'src/layouts/AppStateContextLayout/utils'

import { backgroundJobBarVariantClassName, getJobBarContent } from './backgroundJobMessages'
import { useBackgroundJobs } from './BackgroundJobsProvider'
import PillChromeIconAction from './PillChromeIconAction'
import { TrackedBackgroundJob } from './types'

type Props = {
  /** Inline in TopBar vs fixed beside the floating AI Assistant pill. */
  placement?: 'inline' | 'floating'
}

/** Mirror `AIAssistantPill` shell overrides — do not change AIAssistantPill itself. */
const aiAssistantPillShellClassName =
  '!h-auto !rounded-full !py-1 !shadow-sm !transition-all !duration-200 after:!rounded-full after:!inset-shadow-none'

const getJobStatusIconKey = (job: TrackedBackgroundJob): IconKey | undefined => {
  if (isActiveBackgroundJobStatus(job.status)) {
    return 'FaSpinnerThird'
  }
  if (job.status === BackgroundJobStatusValue.Completed) {
    return 'FaCheck'
  }
  if (job.status === BackgroundJobStatusValue.PartiallyCompleted) {
    return 'FaCircleExclamation'
  }
  if (job.status === BackgroundJobStatusValue.Failed) {
    return 'FaCircleXmark'
  }
  if (job.status === BackgroundJobStatusValue.Cancelled) {
    return 'FaBan'
  }

  return undefined
}

const BackgroundJobTopBarStatus = ({ placement = 'inline' }: Props) => {
  const isGov = useIsGov()
  const { notification } = useNotification()
  const { trackedJobs, cancelJob, dismissJob } = useBackgroundJobs()

  const job = useMemo(() => trackedJobs[0], [trackedJobs])

  const handleCancel = useCallback(
    async (event: React.MouseEvent) => {
      event.stopPropagation()
      if (!job) {
        return
      }
      try {
        await cancelJob(job.id)
      } catch (error) {
        console.error(error)
        notification.error({ message: 'Could not cancel background job. Please try again.' })
      }
    },
    [cancelJob, job, notification]
  )

  const handleDismiss = useCallback(
    async (event: React.MouseEvent) => {
      event.stopPropagation()
      if (!job) {
        return
      }
      try {
        await dismissJob(job.id)
      } catch (error) {
        console.error(error)
        notification.error({ message: 'Could not dismiss background job. Please try again.' })
      }
    },
    [dismissJob, job, notification]
  )

  const pill = useMemo(() => {
    if (!job) {
      return null
    }

    const content = getJobBarContent(job)
    const isInProgress = isActiveBackgroundJobStatus(job.status)
    const isComplete = job.isInTerminalStatus
    const statusIconKey = getJobStatusIconKey(job)
    const isStatusSpinner = isActiveBackgroundJobStatus(job.status)
    const hasTrailingAction = isInProgress || isComplete

    return (
      <span
        aria-label={content.message}
        className={twMerge(
          'relative inline-flex max-w-[400px] min-w-0 shrink-0 items-center gap-1.5 border whitespace-nowrap',
          aiAssistantPillShellClassName,
          hasTrailingAction ? '!pr-1 !pl-2.5' : '!px-3',
          backgroundJobBarVariantClassName(content.variant)
        )}
        role="status"
      >
        {statusIconKey && (
          <Icon
            icon={statusIconKey}
            className={twMerge('size-3.5 shrink-0', isStatusSpinner && 'animate-spin')}
          />
        )}
        <VStack align="start" className="min-w-0 flex-1" gap={0}>
          <T.Body className="truncate text-[11px] leading-none font-medium">
            {content.message}
          </T.Body>
          {content.description && (
            <T.Body className="truncate text-[10px] leading-none font-medium opacity-80">
              {content.description}
            </T.Body>
          )}
        </VStack>
        {isInProgress && (
          <PillChromeIconAction
            ariaLabel="Cancel background job"
            icon="FaStop"
            onClick={handleCancel}
            tooltip="Cancel"
          />
        )}
        {isComplete && (
          <PillChromeIconAction
            ariaLabel="Dismiss background job"
            icon="FaXmark"
            onClick={handleDismiss}
            tooltip="Dismiss"
          />
        )}
      </span>
    )
  }, [handleCancel, handleDismiss, job])

  if (!isGov || !pill) {
    return null
  }

  if (placement === 'floating') {
    return (
      <span className="pointer-events-auto absolute top-1/2 right-full mr-2 max-w-[min(400px,calc(100vw-180px))] shrink-0 -translate-y-1/2">
        {pill}
      </span>
    )
  }

  return <span className="pointer-events-auto shrink-0">{pill}</span>
}

export default React.memo(BackgroundJobTopBarStatus)
